import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { getNotificaciones, marcarNotificacionLeida, marcarTodasLeidas } from '../services/api';
import SearchBar from './ui/SearchBar';
import Button from './ui/Button';
import Dropdown, { DropdownItem } from './ui/Dropdown';
import './Header.css';

function Header({ user, onLogout }) {
  const navigate = useNavigate();
  const [notificaciones, setNotificaciones] = useState([]);
  const [showNotificaciones, setShowNotificaciones] = useState(false);
  const [showAvatarMenu, setShowAvatarMenu] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const notificacionesRef = useRef(null);
  const avatarMenuRef = useRef(null);

  useEffect(() => {
    if (user) {
      cargarNotificaciones();
      const interval = setInterval(cargarNotificaciones, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificacionesRef.current && !notificacionesRef.current.contains(e.target)) {
        setShowNotificaciones(false);
      }
    };
    if (showNotificaciones) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showNotificaciones]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (avatarMenuRef.current && !avatarMenuRef.current.contains(e.target)) {
        setShowAvatarMenu(false);
      }
    };
    if (showAvatarMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showAvatarMenu]);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const cargarNotificaciones = async () => {
    try {
      const response = await getNotificaciones();
      setNotificaciones(response.data);
    } catch (error) {
      console.error('Error al cargar notificaciones:', error);
    }
  };

  const handleNotificacionClick = async (notif) => {
    try {
      if (!notif.leida) {
        await marcarNotificacionLeida(notif.id);
      }
      if (notif.incidencia_id) {
        navigate(`/incidencias/${notif.incidencia_id}`);
      } else if (notif.anuncio_id) {
        navigate(`/tablon/${notif.anuncio_id}`);
      }
      setShowNotificaciones(false);
      cargarNotificaciones();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('Usuario aceptó instalar la PWA');
    }

    setDeferredPrompt(null);
  };

  const handleMarcarTodasLeidas = async () => {
    try {
      await marcarTodasLeidas();
      cargarNotificaciones();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const noLeidas = notificaciones.filter(n => !n.leida).length;

  const isConserje = user.rol === 'conserje';
  const modulos = user.modulos_activos || {};
  const tieneModulo = (m) => modulos[m] !== false;  // sin dato = grandfathered = true

  const tabItems = [
    {
      key: 'fichajes',
      to: '/fichajes',
      label: 'Fichajes',
      show: isConserje && tieneModulo('fichajes'),
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
      )
    },
    {
      key: 'calendario',
      to: '/ausencias',
      label: 'Calendario',
      show: isConserje,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      )
    },
    {
      key: 'incidencias',
      to: '/incidencias',
      label: 'Incidencias',
      show: true,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      )
    },
    {
      key: 'reservas',
      to: '/reservas',
      label: 'Reservas',
      show: !isConserje && tieneModulo('reservas'),
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      )
    },
    {
      key: 'dashboard',
      to: '/dashboard',
      label: 'Inicio',
      show: !isConserje,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      )
    },
    {
      key: 'tablon',
      to: '/tablon',
      label: 'Tablón',
      show: user.rol !== 'proveedor' && !isConserje,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      )
    },
    {
      key: 'documentos',
      to: '/documentos',
      label: 'Documentos',
      show: user.rol !== 'proveedor' && !isConserje,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      )
    }
  ].filter(item => item.show);

  return (
    <>
      {/* Topbar */}
      <div className="topbar">
        <div className="topbar-brand" onClick={() => navigate('/dashboard')} role="button" tabIndex={0}>
          <img src="/logo512.png" alt="HabitaPro" className="topbar-brand-logo" />
          <span className="topbar-brand-text">HabitaPro</span>
        </div>

        <div className="topbar-left">
          <SearchBar />
        </div>

        <div className="topbar-actions">
          {isConserje && (
            <Button variant="primary" size="md" icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            } onClick={() => navigate('/incidencias/nueva')}>
              Nueva Incidencia
            </Button>
          )}
          {user.rol !== 'proveedor' && !isConserje && (
            <Dropdown
              trigger={
                <Button variant="primary" size="md" icon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                }>
                  Accesos Rápidos
                </Button>
              }
            >
              <DropdownItem
                icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>}
                onClick={() => navigate('/incidencias/nueva')}
              >
                Nueva Incidencia
              </DropdownItem>
              {(user.rol === 'administrador' || user.rol === 'empleado' || user.rol === 'presidente') && (
                <DropdownItem
                  icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>}
                  onClick={() => navigate('/tablon/nuevo')}
                >
                  Nuevo Anuncio
                </DropdownItem>
              )}
              {tieneModulo('reservas') && (
                <DropdownItem
                  icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>}
                  onClick={() => navigate('/reservas')}
                >
                  Nueva Reserva
                </DropdownItem>
              )}

              {(user.rol === 'administrador' || user.rol === 'empleado' || user.rol === 'presidente') && [
                  <hr key="sep" style={{margin: '6px 12px', border: 'none', borderTop: '1px solid #E5E7EB'}} />,
                  <p key="cat" style={{fontSize: '10px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '2px 14px 4px', margin: 0}}>Administración</p>,
                  <DropdownItem key="usuarios" icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>} onClick={() => navigate('/usuarios')}>Usuarios</DropdownItem>,
                  <DropdownItem key="comunidades" icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>} onClick={() => navigate('/comunidades')}>Comunidades</DropdownItem>,
                  tieneModulo('proveedores') && <DropdownItem key="proveedores" icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-4 0v2" /></svg>} onClick={() => navigate('/proveedores')}>Proveedores</DropdownItem>,
                  <DropdownItem key="invitaciones" icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>} onClick={() => navigate('/invitaciones')}>Invitaciones</DropdownItem>,
                  (user.rol === 'administrador' || user.rol === 'empleado') && <DropdownItem key="permisos" icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>} onClick={() => navigate('/permisos')}>Permisos</DropdownItem>,
                  (user.rol === 'administrador' || user.rol === 'empleado' || user.rol === 'presidente') && <DropdownItem key="notificaciones-push" icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>} onClick={() => navigate('/notificaciones-push')}>Notificaciones Push</DropdownItem>,
                  user.is_superadmin && <DropdownItem key="gestion-admins" icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>} onClick={() => navigate('/gestion-admins')}>Gestión de admins</DropdownItem>,
                  tieneModulo('fichajes') && <DropdownItem key="fichajes" icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>} onClick={() => navigate('/fichajes')}>Fichajes</DropdownItem>,
                  (user.rol === 'administrador' || user.rol === 'empleado' || user.rol === 'conserje') && <DropdownItem key="ausencias" icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="14" x2="8" y2="18"/><line x1="12" y1="14" x2="12" y2="18"/></svg>} onClick={() => navigate('/ausencias')}>Calendario</DropdownItem>
              ]}
            </Dropdown>
          )}

          {deferredPrompt && !isInstalled && (
            <Button variant="secondary" size="md" className="hide-mobile" onClick={handleInstallClick}>
              Instalar App
            </Button>
          )}

          {/* Notifications */}
          <div className="notifications-container" ref={notificacionesRef}>
            <button
              className="notification-btn"
              onClick={() => setShowNotificaciones(!showNotificaciones)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
              </svg>
              {noLeidas > 0 && (
                <span className="notification-badge">{noLeidas}</span>
              )}
            </button>

            {showNotificaciones && (
              <div className="notifications-dropdown">
                <div className="notifications-header">
                  <h3>Notificaciones</h3>
                  <span className="notifications-count">{noLeidas} nuevas</span>
                </div>
                {noLeidas > 0 && (
                  <button className="mark-all-read-btn" onClick={handleMarcarTodasLeidas}>
                    Marcar todas como leídas
                  </button>
                )}
                <div className="notifications-list">
                  {notificaciones.length === 0 ? (
                    <div className="notification-empty">
                      No hay notificaciones
                    </div>
                  ) : (
                    notificaciones.map(notif => {
                      const meta = notif.incidencia_id
                        ? { icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>, color: '#f97316', bg: 'rgba(249,115,22,0.08)' }
                        : notif.anuncio_id
                          ? { icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>, color: '#10b981', bg: 'rgba(16,185,129,0.08)' }
                          : { icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>, color: '#6b7280', bg: 'rgba(107,114,128,0.08)' };
                      return (
                      <div
                        key={notif.id}
                        className={`notification-item ${!notif.leida ? 'unread' : ''}`}
                        onClick={() => handleNotificacionClick(notif)}
                        style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}
                      >
                        <div style={{
                          width: '32px', height: '32px', borderRadius: '50%',
                          background: meta.bg, color: meta.color,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px'
                        }}>
                          {meta.icon}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}>{notif.mensaje}</p>
                          <span className="notification-time">
                            {new Date(notif.fecha_creacion).toLocaleString('es-ES')}
                          </span>
                        </div>
                      </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Avatar con menu de salir */}
          <div className="topbar-avatar-wrapper" ref={avatarMenuRef}>
            <button
              type="button"
              className="topbar-avatar topbar-avatar--btn"
              onClick={() => setShowAvatarMenu(v => !v)}
              aria-label="Menú de usuario"
            >
              {user.nombre ? user.nombre.charAt(0).toUpperCase() : 'U'}
            </button>
            {showAvatarMenu && (
              <div className="topbar-avatar-menu">
                <div className="topbar-avatar-menu-header">
                  <strong>{user.nombre || 'Usuario'}</strong>
                  <span>{user.email || user.rol || 'vecino'}</span>
                </div>
                <hr className="topbar-avatar-menu-divider" />
                <button
                  type="button"
                  className="topbar-avatar-menu-item"
                  onClick={() => { setShowAvatarMenu(false); navigate('/ajustes'); }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  Mi perfil
                </button>
                <button
                  type="button"
                  className="topbar-avatar-menu-item"
                  onClick={() => { setShowAvatarMenu(false); navigate('/seguridad'); }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  Seguridad
                </button>
                <hr className="topbar-avatar-menu-divider" />
                <button
                  type="button"
                  className="topbar-avatar-menu-item topbar-avatar-menu-item--logout"
                  onClick={() => { setShowAvatarMenu(false); onLogout(); }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <nav className="bottom-tabs" aria-label="Navegación principal">
        {tabItems.map((item) => (
          <NavLink
            key={item.key}
            to={item.to}
            className={({ isActive }) => `bottom-tab-item ${isActive ? 'active' : ''}`}
          >
            <span className="bottom-tab-icon">{item.icon}</span>
            <span className="bottom-tab-label">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </>
  );
}

export default Header;
